/**
 * HierarchyNavigationContext
 * Persists block diagram navigation state across workshop pages.
 *
 * When users drill down into a component in one workshop, the same view
 * persists when navigating to other workshops (Function, FM, Causes, Mitigations).
 *
 * State is also persisted to sessionStorage so it survives page refreshes.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { logger } from '@/utils/logger';

// Storage key for sessionStorage. Exported (F2) so the FMEA tab-shell embeds
// (useEmbeddedWorkshopScope.ts) can write a hard-reload safety-net restore
// directly to sessionStorage without duplicating the literal key.
export const HIERARCHY_NAVIGATION_STORAGE_KEY = 'hierarchy-navigation-state';
const STORAGE_KEY = HIERARCHY_NAVIGATION_STORAGE_KEY;

export interface HierarchyNavigationState {
  currentParentId: string | null;
  selectedItemId: string | null;
}

interface HierarchyNavigationContextValue {
  state: HierarchyNavigationState;
  setCurrentParentId: (id: string | null) => void;
  setSelectedItemId: (id: string | null) => void;
  setNavigationState: (state: Partial<HierarchyNavigationState>) => void;
  reset: () => void;
}

const defaultState: HierarchyNavigationState = {
  currentParentId: null,
  selectedItemId: null,
};

const HierarchyNavigationContext = createContext<HierarchyNavigationContextValue | null>(null);

/**
 * Load state from sessionStorage
 */
function loadPersistedState(): HierarchyNavigationState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        currentParentId: parsed.currentParentId ?? null,
        selectedItemId: parsed.selectedItemId ?? null,
      };
    }
  } catch (e) {
    logger.warn('Failed to load hierarchy navigation state', e, { module: 'HierarchyNavigationContext' });
  }
  return defaultState;
}

/**
 * Save state to sessionStorage
 */
function persistState(state: HierarchyNavigationState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    logger.warn('Failed to persist hierarchy navigation state', e, { module: 'HierarchyNavigationContext' });
  }
}

export function HierarchyNavigationProvider({ children }: { children: React.ReactNode }) {
  // Initialize from sessionStorage
  const [state, setState] = useState<HierarchyNavigationState>(loadPersistedState);

  // Persist state changes to sessionStorage
  useEffect(() => {
    persistState(state);
  }, [state]);

  const setCurrentParentId = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, currentParentId: id }));
  }, []);

  const setSelectedItemId = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, selectedItemId: id }));
  }, []);

  const setNavigationState = useCallback((newState: Partial<HierarchyNavigationState>) => {
    setState(prev => ({ ...prev, ...newState }));
  }, []);

  const reset = useCallback(() => {
    setState(defaultState);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    state,
    setCurrentParentId,
    setSelectedItemId,
    setNavigationState,
    reset,
  }), [state, setCurrentParentId, setSelectedItemId, setNavigationState, reset]);

  return (
    <HierarchyNavigationContext.Provider value={contextValue}>
      {children}
    </HierarchyNavigationContext.Provider>
  );
}

/**
 * Hook to access hierarchy navigation context
 * Returns null if used outside of provider (allows graceful fallback)
 */
export function useHierarchyNavigation(): HierarchyNavigationContextValue | null {
  return useContext(HierarchyNavigationContext);
}

/**
 * Hook that requires the context (throws if not available)
 */
export function useHierarchyNavigationRequired(): HierarchyNavigationContextValue {
  const context = useContext(HierarchyNavigationContext);
  if (!context) {
    throw new Error('useHierarchyNavigationRequired must be used within HierarchyNavigationProvider');
  }
  return context;
}

export default HierarchyNavigationContext;
